import { Button, Form } from 'react-bootstrap';
import { Filter, FilterRemove } from '@carbon/icons-react';
import React, { FunctionComponent, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import Select, { Theme } from 'react-select';
import { SelectOption, UserRoles } from '../Shared/SharedModels';

import { DisplayUser } from './TeamMemberModels';
import { UserProfileContext } from '../Shared/contexts/UserProfile';

interface UserFilterProps {
    masterUserList: DisplayUser[];
    setFilteredUsers: (users: DisplayUser[]) => void;
}

// Standard ClientInsight role filter options
const standardRoleOptions: SelectOption<number>[] = [
    { label: 'Global Admin', value: UserRoles['Global Admin'] },
    { label: 'Source System Admin', value: UserRoles['Source System Admin'] },
    { label: 'Org Admin', value: UserRoles['Org Admin'] },
    { label: 'Member', value: UserRoles.Member },
];

// CW Insights role filter options
const cwInsightsRoleOptions: SelectOption<number>[] = [
    { label: 'Insights Global Admin', value: UserRoles['Insights Global Admin'] },
    { label: 'Insights Engineer', value: UserRoles['Insights Engineer'] },
    { label: 'Insights Analyst', value: UserRoles['Insights Analyst'] },
    { label: 'Insights Viewer', value: UserRoles['Insights Viewer'] },
];

const statusOptions: SelectOption<string>[] = [
    { label: 'Active', value: 'Active' },
    { label: 'Inactive', value: 'Inactive' },
    { label: 'Pending', value: 'Pending' },
];


const UserFilters: FunctionComponent<UserFilterProps> = (props: UserFilterProps) => {
    const { masterUserList: masterUserList, setFilteredUsers: setUsers } = props;
    const userProfile = useContext(UserProfileContext);
    const [userName, setUserName] = useState('');
    const [userRole, setUserRole] = useState<SelectOption<number>[]>([]);
    const [userJobTitle, setUserJobTitle] = useState('');
    const [areFiltersApplied, setAreFiltersApplied] = useState(false);
    const [userStatus, setUserStatus] = useState<SelectOption<string>[]>([]);

    // Use appropriate role options based on customer type
    const roleOptions = useMemo(() => {
        return userProfile.environmentConfig.cwInsightsCustomer
            ? cwInsightsRoleOptions
            : standardRoleOptions;
    }, [userProfile.environmentConfig.cwInsightsCustomer]);

    const formSelectTheme = useCallback(
        (theme: Theme) => ({
            ...theme,
            borderRadius: 8,
        }),
        []
    );

    const clearFilters = useCallback(() => {
        setUserName('');
        setUserRole([]);
        setUserJobTitle('');
        setUserStatus([]);
    }, []);

    useEffect(() => {
        setAreFiltersApplied(!!userName || userRole.length >0 || !!userJobTitle || userStatus.length > 0);
    }, [userName, userRole, userJobTitle, userStatus]);

    useEffect(() => {
        let filteredUsers = masterUserList;

        if (userName) {
            filteredUsers = filteredUsers.filter((user) => {
                return user.name?.toLowerCase().includes(userName.toLowerCase());
            });
        }

        if (userJobTitle) {
            filteredUsers = filteredUsers.filter((user) => {
                return user.jobTitle?.toLowerCase().includes(userJobTitle.toLowerCase());
            });
        }

        if (userRole.length > 0) {
            filteredUsers = filteredUsers.filter((user) => {
                return userRole.some((role) => {
                    return role.value === user.fullUser.roleKey;
                });
            });
        }

        if (userStatus.length > 0) {
            filteredUsers = filteredUsers.filter((user) => {
                return userStatus.some((status) => {
                    return status.value === user.status;
                });
            });
        }

        setUsers(filteredUsers);
    }, [masterUserList, setUsers, userName, userRole, userJobTitle, userStatus]);

    return (
        <>
            <div className="filtersAppliedContainer">
                <Button variant="link" onClick={clearFilters} disabled={!areFiltersApplied} className={`${areFiltersApplied ? 'filters-applied' : 'no-filter'} filter-button`}>
                    {areFiltersApplied ? (
                        <>
                            <img src="filter--reset.svg" className="button-icon-padding" alt="" height={20} width={20} />
                            <span className="no-filters">Reset All Filters</span>
                        </>
                    ) : (
                        <>
                            <img src="filter--solid.svg" className="button-icon-padding" alt="" height={20} width={20} />
                            <span>No Filters Applied</span>
                        </>
                    )}
                </Button>
            </div>
            <Form className="filterFormContainer">
                <Form.Group className="data-table-filter">
                    <Form.Label htmlFor="NameFilter">Team Member Name</Form.Label>
                    <Form.Control
                        type="text"
                        placeholder="Enter name..."
                        id="NameFilter"
                        value={userName}
                        onChange={(event) => {
                            setUserName(event.target.value);
                        }}
                    />
                </Form.Group>
                <Form.Group className="data-table-filter">
                    <Form.Label htmlFor="RoleFilterLabel">Role</Form.Label>
                    <Select
                        aria-labelledby="RoleFilterLabel"
                        isMulti
                        className="filteredSelect"
                        classNamePrefix="base-select"
                        placeholder="Select role..."
                        id="RoleFilter"
                        value={userRole}
                        theme={formSelectTheme}
                        options={roleOptions}
                        onChange={(selectedOptions) => {
                            setUserRole(selectedOptions as SelectOption<number>[]);
                        }}
                        menuPlacement="auto"
                    />
                </Form.Group>
                <Form.Group className="data-table-filter">
                    <Form.Label htmlFor="JobTitleFilter">Job Title</Form.Label>
                    <Form.Control
                        type="text"
                        placeholder="Job title..."
                        id="JobTitleFilter"
                        value={userJobTitle}
                        onChange={(event) => {
                            setUserJobTitle(event.target.value);
                        }}
                    />
                </Form.Group>
                <Form.Group className="data-table-filter">
                    <Form.Label htmlFor="StatusFilterLabel">Status</Form.Label>
                    <Select
                        aria-labelledby="StatusFilterLabel"
                        data-testid="StatusFilter"
                        isMulti
                        classNamePrefix="base-select"
                        className="filteredSelect"
                        placeholder="Select status..."
                        id="StatusFilterLabel"
                        value={userStatus}
                        theme={formSelectTheme}
                        options={statusOptions}
                        onChange={(selectedOptions) => {
                            setUserStatus(selectedOptions as SelectOption<string>[]);
                        }}
                        menuPlacement="auto"
                    />
                </Form.Group>
            </Form>
        </>
    );
};

export default UserFilters;
